import { ReactElement, ReactNode } from 'react';
import { AuthenticationController } from './AuthenticationController';
import { Amplify, ResourcesConfig } from 'aws-amplify';
import { ProvisionedConstants } from 'src/constants';

/**
 * The custom identity provider name for Amazon Federated Sign-In.
 * @constant {string}
 */
const USER_POOL_IDP = 'AmazonFederate';

/**
 * The constructed domain URL
 */
const DOMAIN_URL = `${ProvisionedConstants.USER_POOL_DOMAIN}.auth.${ProvisionedConstants.USER_POOL_REGION}.amazoncognito.com`;

/**
 * The OAuth 2.0 scopes for the Cognito User Pool.
 * @constant {string[]}
 */
const USER_POOL_AUTH_SCOPES = ['openid'];

/**
 * The OAuth 2.0 response type for the Cognito User Pool.
 * @constant {string}
 */
const USER_POOL_AUTH_RESPONSE_TYPE = 'code';

/**
 * Configuration object for AWS Amplify resources.
 * Defines authentication and API settings.
 *
 * @constant {ResourcesConfig}
 *
 * More info available here: {@link https://docs.amplify.aws/gen1/react/build-a-backend/auth/set-up-auth}
 */
const amplifyResourcesConfig: ResourcesConfig = {
  Auth: {
    Cognito: {
      userPoolId: ProvisionedConstants.USER_POOL_ID,
      userPoolClientId: ProvisionedConstants.USER_POOL_CLIENT_ID,
      identityPoolId: ProvisionedConstants.IDENTITY_POOL_ID,
      loginWith: {
        oauth: {
          domain: DOMAIN_URL,
          scopes: USER_POOL_AUTH_SCOPES,
          redirectSignIn: [window.location.origin],
          redirectSignOut: [],
          responseType: USER_POOL_AUTH_RESPONSE_TYPE,
          providers: [{ custom: USER_POOL_IDP }],
        },
      },
    },
  },
  API: {
    /**
     * Add IAM-authenticated API endpoints here using Amplify API library.
     *
     * Note: API defaults to IAM authorization with automatic request signing.
     */
    REST: {},
  },
};

/**
 * Additional options for the Amplify library.
 * Used for configuring endpoint-specific headers.
 *
 * @constant {Object}
 */
const amplifyLibraryOptions = {
  API: {
    /**
     * Configure endpoint specific custom headers here.
     */
    REST: {},
  },
};

/**
 * Wraps the application with authentication context and configures AWS Amplify.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {ReactNode} [props.children] - Child components to render within the authentication context.
 * @returns {ReactElement} The AuthenticationController component with children.
 *
 * @example
 * <AuthenticationWrapper>
 * <App />
 * </AuthenticationWrapper>
 */
const AuthenticationWrapper = (props: { children?: ReactNode }): ReactElement => {
  Amplify.configure(amplifyResourcesConfig, amplifyLibraryOptions);

  return <AuthenticationController>{props.children}</AuthenticationController>;
};

export { USER_POOL_IDP, AuthenticationWrapper };
